"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import type { RecipeBox } from "@/lib/types";
import { canAddRecipes } from "@/lib/recipe-box-permissions";
import { Loader2 } from "lucide-react";
import { useState } from "react";

export type CloneRecipeDialogProps = Readonly<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recipeTitle: string;
  sourceRecipeBoxId: string;
  recipeBoxes: RecipeBox[];
  userId: string | null;
  onClone: (targetRecipeBoxId: string) => Promise<void>;
  isCloning?: boolean;
}>;

export function CloneRecipeDialog({
  open,
  onOpenChange,
  recipeTitle,
  sourceRecipeBoxId,
  recipeBoxes,
  userId,
  onClone,
  isCloning = false,
}: CloneRecipeDialogProps) {
  const [selectedBoxId, setSelectedBoxId] = useState<string>("");

  // Filter boxes where user can add recipes (owner or member)
  const availableBoxes = recipeBoxes.filter(
    (box) => box.id !== sourceRecipeBoxId && canAddRecipes(box, userId),
  );

  const handleClone = async () => {
    if (!selectedBoxId) {
      return;
    }
    await onClone(selectedBoxId);
    setSelectedBoxId("");
  };

  const handleCancel = () => {
    setSelectedBoxId("");
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Clone Recipe</AlertDialogTitle>
          <AlertDialogDescription>
            Select a recipe box to clone &quot;{recipeTitle}&quot; to. The cloned recipe will be owned by you.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-4">
          {availableBoxes.length === 0 ? (
            <div className="rounded-md bg-muted p-4 text-sm text-muted-foreground">
              You don&apos;t have access to any other recipe boxes where you can add recipes.
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="recipe-box-select">Destination Recipe Box</Label>
                <Select value={selectedBoxId} onValueChange={setSelectedBoxId}>
                  <SelectTrigger id="recipe-box-select">
                    <SelectValue placeholder="Select a recipe box..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableBoxes.map((box) => (
                      <SelectItem key={box.id} value={box.id}>
                        {box.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground">
                {recipeTitle.includes("recipe") ? (
                  <>The recipes will be cloned with &quot;Copy of&quot; prefix and you will be the owner.</>
                ) : (
                  <>The recipe will be cloned with &quot;Copy of&quot; prefix and you will be the owner.</>
                )}
              </p>
            </>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel} disabled={isCloning}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleClone}
            disabled={!selectedBoxId || isCloning || availableBoxes.length === 0}
          >
            {isCloning ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Cloning...
              </>
            ) : (
              "Clone Recipe"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

